import { useSelector } from "react-redux";

import { useGetUserProfileAPI } from "@/api/userProfile/useUserProfileAPI";
import { AnalysisIcon } from "@/assets/icons";
import MapMenuItemControl from "@/components/map/menuItemControl";
import { RootState } from "@/store";
import { AnalysisJobStatus } from "@/types/analysis";

import { twMerge } from "tailwind-merge";

interface Props {
  onToggleAnalysisJobList: () => void;
}

export default function AnalysisJobListMapMenuControl({
  onToggleAnalysisJobList
}: Readonly<Props>) {
  const { data: userProfileData } = useGetUserProfileAPI();

  const { showAnalysisJobList } = useSelector((state: RootState) => state.ui);
  const { unOpenedJobIds, analysisJobs } = useSelector(
    (state: RootState) => state.analysisJob
  );
  const showProgressBar =
    analysisJobs.some(
      (job) =>
        [AnalysisJobStatus.IN_PROGRESS, AnalysisJobStatus.QUEUED].includes(
          job.status
        ) &&
        job.created_by.toLowerCase() ===
          userProfileData?.full_name.toLowerCase()
    ) || unOpenedJobIds.length > 0;
  const progress = unOpenedJobIds.length > 0 ? 100 : 35;

  return (
    <div className="relative top-[30px] right-[24px] z-[99]">
      <div
        className={twMerge(
          "absolute top-0 right-0 flex flex-col",
          "items-center overflow-hidden"
        )}
        data-testid="analysis-job-control"
      >
        <MapMenuItemControl
          className={twMerge("rounded-lg border-[1px] border-gainsboro mb-2")}
          onClick={onToggleAnalysisJobList}
          aria-label="Analysis Job Queue"
          tooltipContent="View analysis job queue."
        >
          <div className="relative flex items-center justify-center">
            {showProgressBar && (
              <svg className="absolute w-full h-full" viewBox="0 0 36 36">
                <path
                  data-testid="progress-bar-path"
                  className="text-teal-500"
                  stroke="currentColor"
                  strokeWidth="3"
                  fill="none"
                  strokeLinecap="square"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  strokeDasharray={`${progress}, 100`}
                />
              </svg>
            )}

            <AnalysisIcon
              title="Analysis Job Queue"
              className={twMerge(
                "fill-dim-gray cursor-pointer",
                showAnalysisJobList && "fill-teal-500",
                showProgressBar && "border-2 rounded-full"
              )}
            />
          </div>
        </MapMenuItemControl>
        {unOpenedJobIds.length > 0 && (
          <div
            data-testid="analysis-red-dot"
            className="absolute top-0 right-0 h-2 w-2 bg-red-500 rounded-full"
          />
        )}
      </div>
    </div>
  );
}
